import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  Monitor,
  ShoppingCart,
  Shield,
  Server,
  Headphones,
  Users,
  Menu,
  X,
  Award,
} from "lucide-react";
import { servicesData } from "../../data/services";

const iconMap: Record<string, React.ElementType> = {
  "web-development": Monitor,
  "e-procurement": ShoppingCart,
  "security-installations": Shield,
  "ict-systems": Server,
  "it-support": Headphones,
  "agency-representation": Users,
};

const Sidebar = () => {
  const location = useLocation();
  const [open, setOpen] = useState(false);

  const isServicePage = location.pathname.startsWith("/service");
  const isAboutPage = location.pathname.startsWith("/about");

  if (!isServicePage && !isAboutPage) return null;

  const NavItems = (
    <ul className="space-y-1">
      {servicesData.map((service) => {
        const isActive = location.pathname === `/service/${service.id}`;
        const Icon = iconMap[service.id] || Monitor;

        return (
          <li key={service.id}>
            <Link
              to={`/service/${service.id}`}
              onClick={() => setOpen(false)}
              className={`flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-semibold transition-all duration-200 ${
                isActive
                  ? "bg-indigo-600 text-white"
                  : "text-white/60 hover:text-white hover:bg-white/8"
              }`}
            >
              <span
                className={`flex items-center justify-center w-8 h-8 rounded-lg shrink-0 ${
                  isActive
                    ? "bg-white/20"
                    : "bg-white/5"
                }`}
              >
                <Icon className="w-4 h-4" />
              </span>
              {service.title}
            </Link>
          </li>
        );
      })}
    </ul>
  );

  const AboutNavItems = (
    <ul className="space-y-1 mt-6">
      <p className="text-[11px] uppercase tracking-widest text-white/30 px-4 mb-3">
        About Us
      </p>
      {[
        { href: "#founder", icon: Award, label: "Founder's Message" },
        { href: "#team", icon: Users, label: "Our Team" },
      ].map(({ href, icon: Icon, label }) => (
        <li key={href}>
          <a
            href={href}
            onClick={() => setOpen(false)}
            className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-semibold text-white/60 hover:text-white hover:bg-white/8 transition-all duration-200"
          >
            <span className="flex items-center justify-center w-8 h-8 rounded-lg bg-white/5 shrink-0">
              <Icon className="w-4 h-4" />
            </span>
            {label}
          </a>
        </li>
      ))}
    </ul>
  );

  const SidebarContent = (
    <>
      <p className="text-[11px] uppercase tracking-widest text-white/30 px-4 mb-4">
        {isServicePage ? "Explore Solutions" : "Company"}
      </p>
      {isServicePage && NavItems}
      {isAboutPage && AboutNavItems}
    </>
  );

  return (
    <>
      {/* Mobile Toggle */}
      <div className="lg:hidden fixed top-20 right-4 z-50">
        <button
          onClick={() => setOpen(true)}
          className="flex items-center justify-center w-10 h-10 rounded-xl bg-indigo-600 text-white shadow-lg hover:bg-indigo-700 transition"
          aria-label="Open navigation"
        >
          <Menu className="w-5 h-5" />
        </button>
      </div>

      {/* Desktop Sidebar */}
      <aside className="hidden lg:block w-64 shrink-0">
        <div className="sticky top-24 bg-[#1e1642] border border-white/10 rounded-2xl p-5">
          {SidebarContent}
        </div>
      </aside>

      {/* Mobile Drawer */}
      {open && (
        <div
          className="fixed inset-0 z-50 flex"
          role="dialog"
          aria-modal="true"
          aria-label="Navigation menu"
        >
          <div className="absolute inset-0 bg-black/60" onClick={() => setOpen(false)} />

          <div className="relative w-64 bg-[#1e1642] border-r border-white/10 p-5 flex flex-col shadow-2xl">
            <div className="flex items-center justify-between mb-6">
              <span className="text-sm font-bold text-white">
                {isServicePage ? "Solutions" : "About Us"}
              </span>
              <button
                onClick={() => setOpen(false)}
                className="flex items-center justify-center w-8 h-8 rounded-lg bg-white/5 hover:bg-white/10 transition"
                aria-label="Close menu"
              >
                <X className="w-4 h-4 text-white/70" />
              </button>
            </div>
            {SidebarContent}
          </div>
        </div>
      )}
    </>
  );
};

export default Sidebar;